import { useState } from 'react';
import { toast } from 'sonner';
import {
  ArrowClockwise, CaretDown, CaretRight, Check, ClockCounterClockwise, Sparkle, X,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { devApi } from './devApi';
import { CitationList, ConfidenceBadge } from './Citations';
import { CONCEPT_FIELDS, CONCEPT_LABEL, emptyConcept } from './conceptFields';
import { jstDateTime } from '@/utils/jstDate';

/**
 * フォーム内のAI補助。生成は AIワークスペースと**同じ** POST /{did}/ai-refine
 * （services.dev_concept_ai.refine_concept）を呼ぶ。生成経路を二重に持たない。
 * 提案は下書きとしてフォームに入るだけで、保存は担当者の明示操作のまま。
 */
const AiAssist = ({ devId, onDraft }) => {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [taken, setTaken] = useState({});

  const suggest = async () => {
    setBusy(true);
    setTaken({});
    try {
      // AiWorkspace.js の refine() と同じ呼び出し。リンク済み書類＋メモを材料にする。
      const r = await devApi.aiRefine(devId, {
        notes, document_ids: [], include_linked: true,
      });
      setResult(r);
      if (r.note) toast.error(r.note);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AIの下書きを作れませんでした');
    }
    setBusy(false);
  };

  const take = (row) => {
    onDraft({ [row.key]: row.proposed });
    setTaken((t) => ({ ...t, [row.key]: true }));
  };

  const rows = (result?.fields || []).filter((x) => x.changed);

  return (
    <div className="rounded-md border border-zinc-300 bg-zinc-50" data-testid="dev-concept-ai-assist">
      <button type="button" onClick={() => setOpen(!open)} data-testid="dev-concept-ai-assist-toggle"
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-zinc-100">
        {open ? <CaretDown size={13} /> : <CaretRight size={13} />}
        <Sparkle size={14} weight="duotone" className="text-teal-700" />
        <span className="text-sm font-bold text-zinc-800">AIに下書きを提案してもらう</span>
        <span className="hidden text-xs text-zinc-600 sm:block">
          リンク済みの書類とメモから提案します。保存はされません。
        </span>
      </button>
      {open && (
        <div className="space-y-3 border-t border-zinc-200 p-3">
          <div>
            <Label className="text-xs font-semibold text-zinc-700">メモ（任意）</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="例: 試作1号で温度ムラが±3℃に改善。目標仕様を見直したい。"
              data-testid="dev-concept-ai-assist-notes" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={suggest} disabled={busy}
              data-testid="dev-concept-ai-assist-btn">
              <Sparkle size={14} className="mr-1" /> {busy ? '作成中…' : '下書きを提案してもらう'}
            </Button>
            {result && (
              <Button variant="outline" size="sm" onClick={suggest} disabled={busy}
                data-testid="dev-concept-ai-assist-again-btn">
                <ArrowClockwise size={14} className="mr-1" /> もう一度
              </Button>
            )}
            {rows.length > 0 && (
              <Button variant="outline" size="sm" data-testid="dev-concept-ai-assist-take-all"
                onClick={() => {
                  onDraft(Object.fromEntries(rows.map((x) => [x.key, x.proposed])));
                  setTaken(Object.fromEntries(rows.map((x) => [x.key, true])));
                }}>
                すべて下書きに入れる
              </Button>
            )}
          </div>
          {result && (
            <div className="space-y-2" data-testid="dev-concept-ai-assist-result">
              {result.note ? (
                // AIが失敗したときに「変更不要」と読ませてはいけない。
                <p className="rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-900"
                  data-testid="dev-concept-ai-assist-note">
                  {result.note}
                </p>
              ) : (
                <p className="text-xs text-zinc-700">{result.disclaimer}</p>
              )}
              {!result.note && (rows.length === 0 ? (
                <p className="text-sm text-zinc-700" data-testid="dev-concept-ai-assist-nochange">
                  書き換えたほうがよい項目は見つかりませんでした。
                </p>
              ) : rows.map((x) => (
                <div key={x.key} className="rounded-md border border-teal-300 bg-teal-50/40 p-3"
                  data-testid={`dev-concept-ai-assist-row-${x.key}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-bold text-zinc-900">{x.label}</p>
                    <ConfidenceBadge level={x.confidence} />
                    <Button size="sm" variant={taken[x.key] ? 'outline' : 'default'}
                      onClick={() => take(x)}
                      data-testid={`dev-concept-ai-assist-take-${x.key}`}>
                      {taken[x.key] ? '下書きに入れました' : '下書きに入れる'}
                    </Button>
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-zinc-900">{x.proposed}</p>
                  {x.reason && <p className="mt-1 text-xs text-zinc-600">理由: {x.reason}</p>}
                  <CitationList citations={x.citations} />
                </div>
              )))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** 構想提案の手入力フォーム。担当者は扱わない（別の経路が持つ）。 */
export const ConceptEditForm = ({ devId, concept, onSaved, onCancel }) => {
  const [f, setF] = useState({ ...emptyConcept(), ...(concept || {}) });
  const [busy, setBusy] = useState(false);
  const [aiDrafted, setAiDrafted] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const missing = CONCEPT_FIELDS
    .filter((x) => x.required && !String(f[x.key] || '').trim())
    .map((x) => x.label);

  const save = async () => {
    setBusy(true);
    try {
      await devApi.patch(devId, {
        concept: f,
        concept_note: aiDrafted ? '手入力で更新（AIの下書きを含む）' : '手入力で更新',
      });
      toast.success('構想提案を更新しました');
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || '更新に失敗しました');
    }
    setBusy(false);
  };

  return (
    <div className="space-y-3 p-4" data-testid="dev-concept-edit-form">
      <AiAssist devId={devId} onDraft={(patch) => {
        setF((x) => ({ ...x, ...patch }));
        setAiDrafted(true);
      }} />
      {missing.length > 0 && (
        <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
          data-testid="dev-concept-edit-missing">
          未入力の必須項目: {missing.join('、')}（未入力でも保存できます。試作は止まりません）
        </p>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        {CONCEPT_FIELDS.map((x) => (
          <div key={x.key} className={x.wide ? 'sm:col-span-2' : ''}>
            <Label className="text-xs font-semibold text-zinc-700">
              {x.label}{x.required ? '（必須）' : '（任意）'}
            </Label>
            {x.type === 'text' ? (
              <Input value={f[x.key] || ''} onChange={(e) => set(x.key, e.target.value)}
                data-testid={`dev-concept-edit-${x.key}`} />
            ) : (
              <Textarea rows={x.rows} value={f[x.key] || ''}
                onChange={(e) => set(x.key, e.target.value)}
                data-testid={`dev-concept-edit-${x.key}`} />
            )}
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel} data-testid="dev-concept-edit-cancel">
          <X size={14} className="mr-1" /> やめる
        </Button>
        <Button size="sm" onClick={save} disabled={busy} data-testid="dev-concept-edit-save">
          <Check size={14} className="mr-1" /> {busy ? '保存中…' : '保存する'}
        </Button>
      </div>
    </div>
  );
};

const fmt = (s) => (s ? jstDateTime(s) : '—');

/** 変更履歴 — 誰が・いつ・何を何から何に変えたか。追記のみ。 */
export const ConceptHistory = ({ history }) => {
  const [open, setOpen] = useState(false);
  const rows = history || [];
  return (
    <div className="border-t border-zinc-200" data-testid="dev-concept-history">
      <button type="button" onClick={() => setOpen(!open)} data-testid="dev-concept-history-toggle"
        className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-zinc-700 hover:bg-zinc-50">
        {open ? <CaretDown size={13} /> : <CaretRight size={13} />}
        <ClockCounterClockwise size={14} className="text-zinc-500" />
        <span className="font-semibold">変更履歴</span>
        <span className="text-xs text-zinc-600">{rows.length}件</span>
      </button>
      {open && (
        rows.length === 0 ? (
          <p className="px-4 pb-3 text-sm text-zinc-600" data-testid="dev-concept-history-empty">
            まだ変更はありません。
          </p>
        ) : (
          <ul className="divide-y divide-zinc-200">
            {rows.map((h) => (
              <li key={h.id} className="px-4 py-2.5" data-testid={`dev-concept-history-${h.id}`}>
                <p className="text-xs text-zinc-600">
                  {fmt(h.changed_at)} · {h.changed_by_name || h.changed_by || '—'}
                  {h.note ? ` · ${h.note}` : ''}
                </p>
                {(h.changed_fields || []).map((k) => (
                  <div key={k} className="mt-1 text-sm">
                    <p className="font-semibold text-zinc-900">{CONCEPT_LABEL(k)}</p>
                    <p className="whitespace-pre-wrap text-xs text-rose-800" data-testid={`dev-concept-history-before-${k}`}>
                      変更前: {(h.before || {})[k] || '（空欄）'}
                    </p>
                    <p className="whitespace-pre-wrap text-xs text-teal-900" data-testid={`dev-concept-history-after-${k}`}>
                      変更後: {(h.after || {})[k] || '（空欄）'}
                    </p>
                  </div>
                ))}
              </li>
            ))}
          </ul>
        )
      )}
    </div>
  );
};
